/**
 * Helpers pour simplifier l'usage du SDK dans les apps HTML non-bundlées.
 *
 * Ces fonctions sont pensées pour des apps qui chargent le SDK via <script>
 * et n'ont pas de framework JS moderne (pas de React, pas de bundler).
 */

import { createCBClient, type CBClient } from '../client';
import { getCurrentAgent, type CurrentAgent } from '../auth';
import type { Session } from '@supabase/supabase-js';

/**
 * Configuration globale pour les apps HTML.
 *
 * Convention : les apps définissent cette config dans window.__CB_CONFIG__
 * pour éviter de hardcoder les credentials dans le code.
 */
export interface CBConfig {
  CB_CORE_URL: string;
  CB_CORE_ANON_KEY: string;
  CB_APP_SOURCE: string;
}

/**
 * Déclare le type de window.__CB_CONFIG__ pour TypeScript.
 */
declare global {
  interface Window {
    __CB_CONFIG__?: CBConfig;
  }
}

/**
 * Crée un client CB à partir de window.__CB_CONFIG__.
 *
 * Convention : les apps HTML définissent leur config dans window.__CB_CONFIG__
 * pour éviter de hardcoder les credentials dans le code.
 *
 * @returns Client CB configuré
 * @throws Si window.__CB_CONFIG__ n'est pas défini ou incomplet
 *
 * @example
 * ```html
 * <script>
 *   window.__CB_CONFIG__ = {
 *     CB_CORE_URL: 'https://yoqbqewkmfrcnaxxoahs.supabase.co',
 *     CB_CORE_ANON_KEY: 'eyJ...',
 *     CB_APP_SOURCE: 'cbimmodoc'
 *   };
 * </script>
 * <script src="https://sdk.mybestplace.immo/v0.2.0/cb-sdk.js"></script>
 * <script>
 *   const cb = CBSDK.createApp();
 *   console.log('Client créé:', cb);
 * </script>
 * ```
 */
export function createApp(): CBClient {
  const config = window.__CB_CONFIG__;

  if (!config) {
    throw new Error(
      'window.__CB_CONFIG__ is not defined. ' +
        'Please define it before calling createApp().\n' +
        'Example:\n' +
        '  window.__CB_CONFIG__ = {\n' +
        '    CB_CORE_URL: "https://xxx.supabase.co",\n' +
        '    CB_CORE_ANON_KEY: "eyJ...",\n' +
        '    CB_APP_SOURCE: "myapp"\n' +
        '  };'
    );
  }

  if (!config.CB_CORE_URL || !config.CB_CORE_ANON_KEY || !config.CB_APP_SOURCE) {
    throw new Error(
      'window.__CB_CONFIG__ is incomplete. Required fields:\n' +
        '  - CB_CORE_URL (string)\n' +
        '  - CB_CORE_ANON_KEY (string)\n' +
        '  - CB_APP_SOURCE (string)'
    );
  }

  return createCBClient({
    supabaseUrl: config.CB_CORE_URL,
    supabaseAnonKey: config.CB_CORE_ANON_KEY,
    appSource: config.CB_APP_SOURCE,
  });
}

/**
 * Options pour requireAuth().
 */
export interface RequireAuthOptions {
  /**
   * URL Supabase (optionnel si window.__CB_CONFIG__ défini).
   */
  supabaseUrl?: string;

  /**
   * Clé anonyme Supabase (optionnel si window.__CB_CONFIG__ défini).
   */
  supabaseAnonKey?: string;

  /**
   * Identifiant de l'app (optionnel si window.__CB_CONFIG__ défini).
   */
  appSource?: string;

  /**
   * Callback appelé quand l'utilisateur est authentifié.
   *
   * @param session - Session Supabase de l'utilisateur
   * @param agent - Profil agent enrichi (avec agences et rôles), ou null si erreur
   */
  onAuthenticated: (session: Session, agent: CurrentAgent | null) => void;

  /**
   * Callback appelé quand l'utilisateur n'est pas authentifié.
   */
  onUnauthenticated: () => void;

  /**
   * Si true, redirige automatiquement vers Google OAuth au lieu d'appeler onUnauthenticated.
   *
   * @default false
   */
  autoLogin?: boolean;

  /**
   * Si true, affiche des logs de debug dans la console.
   *
   * @default false
   */
  debug?: boolean;
}

/**
 * Helper d'authentification pour les apps HTML.
 *
 * Combine la création du client, la vérification de session, et la récupération
 * de l'agent, puis appelle un callback selon l'état d'authentification.
 *
 * Cette fonction écoute aussi les changements d'auth (login/logout) et rappelle
 * les callbacks automatiquement.
 *
 * @param options - Configuration et callbacks
 *
 * @example
 * ```javascript
 * // Exemple 1 : Avec window.__CB_CONFIG__
 * CBSDK.requireAuth({
 *   onAuthenticated: (session, agent) => {
 *     console.log('Connecté:', agent.email);
 *     document.getElementById('app').innerHTML = `
 *       <h1>Bonjour ${agent.prenom} ${agent.nom}</h1>
 *       <p>${agent.agency_members.length} agence(s)</p>
 *     `;
 *   },
 *   onUnauthenticated: () => {
 *     document.getElementById('app').innerHTML = `
 *       <button onclick="location.reload()">Se connecter</button>
 *     `;
 *   }
 * });
 *
 * // Exemple 2 : Avec credentials explicites + auto-login
 * CBSDK.requireAuth({
 *   supabaseUrl: 'https://xxx.supabase.co',
 *   supabaseAnonKey: 'eyJ...',
 *   appSource: 'cbimmodoc',
 *   autoLogin: true,  // Redirige vers Google si non connecté
 *   onAuthenticated: (session, agent) => {
 *     renderApp(agent);
 *   },
 *   onUnauthenticated: () => {
 *     // Pas appelé si autoLogin: true
 *   }
 * });
 * ```
 */
export async function requireAuth(options: RequireAuthOptions): Promise<void> {
  const {
    supabaseUrl,
    supabaseAnonKey,
    appSource,
    onAuthenticated,
    onUnauthenticated,
    autoLogin = false,
    debug = false,
  } = options;

  const log = (...args: any[]) => {
    if (debug) {
      console.log('[CBSDK.requireAuth]', ...args);
    }
  };

  try {
    // 1. Créer le client (depuis options ou window.__CB_CONFIG__)
    log('Creating client...');
    const client =
      supabaseUrl && supabaseAnonKey && appSource
        ? createCBClient({ supabaseUrl, supabaseAnonKey, appSource })
        : createApp();

    log('Client created');

    // 2. Fonction helper pour vérifier la session et appeler le bon callback
    const checkAuth = async () => {
      log('Checking session...');
      const {
        data: { session },
        error: sessionError,
      } = await client.auth.getSession();

      if (sessionError) {
        console.error('[CBSDK.requireAuth] Error getting session:', sessionError);
        onUnauthenticated();
        return;
      }

      if (!session) {
        log('No session found');

        if (autoLogin) {
          log('Auto-login enabled, redirecting to Google...');
          const { error: signInError } = await client.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: window.location.origin + window.location.pathname,
            },
          });

          if (signInError) {
            console.error('[CBSDK.requireAuth] Auto-login failed:', signInError);
            onUnauthenticated();
          }
          // La redirection Google se fait automatiquement, pas besoin de continuer
          return;
        }

        onUnauthenticated();
        return;
      }

      // 3. Session trouvée → récupérer l'agent
      log('Session found, fetching agent...');
      let agent: CurrentAgent | null = null;

      try {
        agent = await getCurrentAgent(client);
        log('Agent fetched:', agent?.email);
      } catch (agentError) {
        console.error('[CBSDK.requireAuth] Error fetching agent:', agentError);
        // On appelle quand même onAuthenticated avec agent = null
        // L'app peut décider quoi faire (afficher erreur, etc.)
      }

      onAuthenticated(session, agent);
    };

    // 4. Vérifier l'auth au démarrage
    await checkAuth();

    // 5. Écouter les changements d'auth (login/logout)
    log('Listening to auth state changes...');
    client.auth.onAuthStateChange((event, session) => {
      log('Auth state changed:', event);

      if (event === 'SIGNED_OUT') {
        onUnauthenticated();
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Re-vérifier l'auth (fetch agent, etc.)
        checkAuth();
      }
    });
  } catch (error) {
    console.error('[CBSDK.requireAuth] Fatal error:', error);
    onUnauthenticated();
  }
}
